import { useLocation, useNavigate } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Plus, Settings } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { normalizeWorkspaceImageUrl } from "@/lib/workspace-image-url";

function workspaceInitials(name: string) {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "W";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[1][0]).toUpperCase();
}

function roleLabel(role: string) {
	if (!role) return "Member";
	return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

interface WorkspaceAvatarProps {
	name: string;
	imageUrl?: string | null;
	size?: "sm" | "md";
	active?: boolean;
}

function WorkspaceAvatar({
	name,
	imageUrl,
	size = "md",
	active,
}: WorkspaceAvatarProps) {
	const dimensions = size === "sm" ? "size-6 text-[10px]" : "size-7 text-xs";
	const baseClasses = `${dimensions} flex shrink-0 items-center justify-center overflow-hidden rounded-md font-semibold tracking-normal`;
	const normalizedImageUrl = normalizeWorkspaceImageUrl(imageUrl);
	if (normalizedImageUrl) {
		return (
			<img
				aria-hidden
				src={normalizedImageUrl}
				alt=""
				className={`${baseClasses} object-cover ring-1 ring-inset ring-border`}
			/>
		);
	}
	return (
		<div
			aria-hidden
			className={`${baseClasses} ${
				active
					? "bg-sidebar-accent text-sidebar-accent-foreground ring-1 ring-inset ring-sidebar-border"
					: "bg-muted text-muted-foreground ring-1 ring-inset ring-border"
			}`}
		>
			{workspaceInitials(name)}
		</div>
	);
}

export function WorkspaceSwitcher({
	collapsed = false,
	variant = "default",
}: {
	collapsed?: boolean;
	variant?: "default" | "compact";
}) {
	const { workspace, workspaces, switchWorkspace, isWorkspacesLoading } =
		useDashboardContext();
	const navigate = useNavigate();
	const location = useLocation();
	const [isSwitching, setIsSwitching] = useState(false);

	if (isWorkspacesLoading || !workspace) return null;

	const handleSwitch = async (workspaceId: string) => {
		if (workspaceId === workspace.id || isSwitching) return;
		const nextWorkspace = workspaces.find((ws) => ws.id === workspaceId);
		if (!nextWorkspace) return;

		setIsSwitching(true);
		try {
			await switchWorkspace(workspaceId);

			// Keep the settings route slug in sync with the newly active workspace.
			if (
				location.pathname.startsWith("/dashboard/workspaces/") &&
				location.pathname.endsWith("/settings")
			) {
				navigate({
					to: "/dashboard/workspaces/$workspaceSlug/settings",
					params: { workspaceSlug: nextWorkspace.slug },
				});
			}

			toast.success(`Switched to ${nextWorkspace.name}`);
		} catch {
			toast.error("Failed to switch workspace");
		} finally {
			setIsSwitching(false);
		}
	};

	const handleCreateWorkspace = () => {
		navigate({ to: "/dashboard/new-workspace" as string });
	};

	return (
		<DropdownMenu>
			{variant === "compact" ? (
				<DropdownMenuTrigger
					className="group flex max-w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 disabled:opacity-60"
					disabled={isSwitching}
				>
					<WorkspaceAvatar
						name={workspace.name}
						imageUrl={workspace.imageUrl}
						size="sm"
					/>
					<div className="min-w-0 flex-1">
						<div className="truncate text-sm font-medium leading-tight text-foreground">
							{workspace.name}
						</div>
					</div>
					<ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
				</DropdownMenuTrigger>
			) : collapsed ? (
				<DropdownMenuTrigger
					disabled={isSwitching}
					aria-label={`Workspace: ${workspace.name}`}
					className="group flex size-9 items-center justify-center rounded-md border border-sidebar-border bg-background transition-colors hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 disabled:opacity-60"
				>
					{workspace.imageUrl ? (
						<WorkspaceAvatar
							name={workspace.name}
							imageUrl={workspace.imageUrl}
							size="sm"
						/>
					) : (
						<ChevronsUpDown className="size-4 text-muted-foreground transition-colors group-hover:text-sidebar-accent-foreground" />
					)}
				</DropdownMenuTrigger>
			) : (
				<DropdownMenuTrigger
					className="group flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/40 disabled:opacity-60"
					disabled={isSwitching}
				>
					<WorkspaceAvatar
						name={workspace.name}
						imageUrl={workspace.imageUrl}
					/>
					<div className="min-w-0 flex-1">
						<div className="truncate text-sm font-medium leading-tight text-foreground">
							{workspace.name}
						</div>
						<div className="truncate text-[10px] uppercase tracking-wide text-muted-foreground leading-tight">
							{roleLabel(workspace.role)}
						</div>
					</div>
					<ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
				</DropdownMenuTrigger>
			)}
			<DropdownMenuContent
				align="start"
				sideOffset={6}
				className="w-64 rounded-md border-border bg-popover p-1.5"
			>
				<div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
					Workspaces
				</div>
				{workspaces.map((ws) => {
					const isActive = ws.id === workspace.id;
					return (
						<DropdownMenuItem
							key={ws.id}
							onClick={() => handleSwitch(ws.id)}
							className={cn(
								"flex items-center gap-2 rounded-md py-1.5",
								isActive && "bg-accent text-accent-foreground",
							)}
						>
							<WorkspaceAvatar
								name={ws.name}
								imageUrl={ws.imageUrl}
								size="sm"
								active={isActive}
							/>
							<div className="min-w-0 flex-1">
								<div className="truncate text-sm font-medium leading-tight">
									{ws.name}
								</div>
								<div className="truncate text-[10px] text-muted-foreground leading-tight">
									{roleLabel(ws.role)}
								</div>
							</div>
							{isActive && (
								<Check className="size-3.5 shrink-0 text-muted-foreground" />
							)}
						</DropdownMenuItem>
					);
				})}
				<DropdownMenuSeparator />
				<DropdownMenuItem onClick={handleCreateWorkspace}>
					<Plus className="size-3.5" />
					Create workspace
				</DropdownMenuItem>
				<DropdownMenuItem
					onClick={() =>
						navigate({
							to: "/dashboard/workspaces/$workspaceSlug/settings",
							params: { workspaceSlug: workspace.slug },
						})
					}
				>
					<Settings className="size-3.5" />
					Workspace settings
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
